import {Action, Module, Mutation} from 'vuex-module-decorators';
import store from '@/store';
import {TimelineInteractionModel, TimelineModel} from '@/models/timeline.model';
import {TimelineApi} from '@/api/timeline.api';
import {take} from 'rxjs/operators';
import {TimelineInteractionApi} from '@/api/timeline-interaction.api';
import {EntityStateAdapter} from '@/store/base.store';
import moment from 'moment-timezone';

export const featureKey = 'timeline';

// @ts-ignore
if (store.state.timeline) {
  store.unregisterModule(featureKey)
}

@Module({
  store,
  dynamic: true,
  name: featureKey,
  namespaced: true,
})
export class ChallengeTimelineStoreModule extends EntityStateAdapter<TimelineModel> {

  private _selectedTimeline: TimelineModel | null = null;

  get timeline(): TimelineModel[] {
    return this._entities;
  }

  get selectedTimeline(): TimelineModel | null {
    return this._selectedTimeline;
  }

  get timelineById(): (id: number) => TimelineModel | undefined {
    return (id: number) => this._entities.find((v) => v.id === id)
  }

  @Mutation
  interactionUpdated(data: { timelineId: number, interaction: TimelineInteractionModel }): void {
    const index = this._entities.findIndex((v) => v.id === data.timelineId);
    if (~index) {
      if (this._entities[index].interaction !== undefined) {
        this._entities[index].interaction = {...data.interaction}
      }
    }
  }

  @Mutation
  interactionDeleted(timelineId: number): void {
    const index = this._entities.findIndex((v) => v.id === timelineId);
    if (~index) {
      this._entities[index].interaction = undefined
    }
  }

  @Mutation
  timelineLoaded(timeline: TimelineModel | null) {
    this._selectedTimeline = timeline;
    if (timeline) {
      super.upsertOne(timeline)
    }
  }

  @Action({commit: 'insertMany', rawError: true})
  async fetchTimeline(challengeId: number): Promise<TimelineModel[]> {
    return await (new TimelineApi(challengeId))
      .all()
      .pipe(take(1))
      .toPromise()
  }

  @Action({commit: 'interactionUpdated', rawError: true})
  async createInteraction(data: {
    challengeId: number,
    timelineId: number,
    payload: TimelineInteractionModel,
  }): Promise<{
    timelineId: number,
    interaction: TimelineInteractionModel,
  }> {
    const timelineApi = new TimelineInteractionApi(data.challengeId, data.timelineId);
    const interaction = await timelineApi.create(data.payload).pipe(take(1)).toPromise();
    return {timelineId: data.timelineId, interaction}
  }

  @Action({commit: 'interactionUpdated', rawError: true})
  async updateInteraction(data: {
    challengeId: number,
    interactionId: number,
    timelineId: number,
    payload: TimelineInteractionModel,
  }): Promise<{
    timelineId: number,
    interaction: TimelineInteractionModel,
  }> {
    const timelineApi = new TimelineInteractionApi(data.challengeId, data.timelineId);
    const interaction = await timelineApi.update(data.payload).pipe(take(1)).toPromise();
    return {timelineId: data.timelineId, interaction}
  }

  @Action({commit: 'interactionDeleted', rawError: true})
  async deleteInteraction(data: {
    challengeId: number,
    interactionId: number,
    timelineId: number,
  }): Promise<number> {
    const timelineApi = new TimelineInteractionApi(data.challengeId, data.timelineId);
    await timelineApi.destroy(data.interactionId).pipe(take(1)).toPromise();
    return data.timelineId
  }

  @Action({commit: 'timelineLoaded', rawError: true})
  async fetchTimelineItem(data: { timelineId: number, challengeId: number }): Promise<TimelineModel> {
    return await new TimelineApi(data.challengeId)
      .findById(data.timelineId)
      .pipe(take(1)).toPromise()
  }
}
